import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import HelpTooltip from '../../components/HelpTooltip';

const upgradePromptStyle = { background: 'rgba(247,124,0,0.1)', border: '1px solid rgba(247,124,0,0.3)', borderRadius: 8, padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-muted)' };

export default function Challenges() {
  const { apiFetch } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [isPaid, setIsPaid] = useState(true);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '' });
  const [editForm, setEditForm] = useState({ name: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [pastLeaderboard, setPastLeaderboard] = useState(null);
  const [viewingChallenge, setViewingChallenge] = useState(null);

  const loadChallenges = async () => {
    try {
      const data = await apiFetch('/api/admin/seasons');
      setChallenges(data.challenges);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); loadChallenges(); }, [apiFetch]); // eslint-disable-line
  useEffect(() => { apiFetch('/api/admin/is-paid').then(d => setIsPaid(d.isPaid)).catch(() => {}); }, [apiFetch]);

  // Auto-fill end date ~11 months after start
  const handleStartDateChange = (startDate) => {
    setForm({ ...form, start_date: startDate });
    if (startDate && !form.end_date) {
      const start = new Date(startDate);
      start.setMonth(start.getMonth() + 11);
      setForm(prev => ({ ...prev, start_date: startDate, end_date: start.toISOString().split('T')[0] }));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/admin/seasons', { method: 'POST', body: JSON.stringify(form) });
      setShowAdd(false);
      setForm({ name: '', start_date: '', end_date: '' });
      loadChallenges();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleActivate = async (id) => {
    const activeChallenge = challenges.find(s => s.active);
    if (activeChallenge) {
      if (!window.confirm(
        `Starting this challenge will end "${activeChallenge.name}". Player challenge stats will reset for the new challenge (lifetime points are preserved). Continue?`
      )) return;
    }
    try {
      await apiFetch(`/api/admin/seasons/${id}/activate`, { method: 'PUT' });
      loadChallenges();
    } catch (err) { alert(err.message); }
  };

  const handleArchive = async (id) => {
    const ch = challenges.find(s => s.id === id);
    if (!window.confirm(`End and archive "${ch?.name}"? The final standings will be preserved.`)) return;
    try {
      await apiFetch(`/api/admin/seasons/${id}/archive`, { method: 'POST' });
      loadChallenges();
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (ch) => {
    if (!window.confirm(`Delete the "${ch.name}" challenge? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/admin/seasons/${ch.id}`, { method: 'DELETE' });
      loadChallenges();
    } catch (err) { alert(err.message); }
  };

  const openEdit = (ch) => {
    setEditingChallenge(ch);
    setEditForm({
      name: ch.name,
      start_date: (ch.start_date || '').split('T')[0],
      end_date: (ch.end_date || '').split('T')[0],
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch(`/api/admin/seasons/${editingChallenge.id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      setEditingChallenge(null);
      loadChallenges();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const viewStandings = async (ch) => {
    setViewingChallenge(ch);
    try {
      const data = await apiFetch(`/api/admin/seasons/${ch.id}/leaderboard`);
      setPastLeaderboard(data.leaderboard);
    } catch (err) {
      console.error(err);
      setPastLeaderboard([]);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date((dateStr || '').split('T')[0] + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) return <div className="admin-page"><div className="loading"><div className="spinner" /></div></div>;

  const activeChallenges = challenges.filter(s => s.active);
  const archivedChallenges = challenges.filter(s => !s.active);

  return (
    <div className="admin-page">
      <div className="flex-between mb-16">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>Challenges</h1>
          <HelpTooltip>
            <h3>Challenges</h3>
            <p>Challenges are time-based competitions for your team. Set a start date, end date, and name, and players compete to earn the most points during that window.</p>
            <p>Points come from completing drills and answering quiz questions. The leaderboard resets with each new challenge, giving every player a fresh start.</p>
            <p>You can have one active challenge at a time. When a challenge ends, you can view the final standings and start a new one. Past challenges stay in your history so you can look back at previous results.</p>
          </HelpTooltip>
        </div>
        {isPaid && <button className="btn btn-orange btn-sm" onClick={() => setShowAdd(true)}>+ New Challenge</button>}
      </div>

      {!isPaid && (
        <div style={{ ...upgradePromptStyle, marginBottom: 16 }}>
          <i className="fas fa-lock" style={{ marginRight: 8 }}></i>
          Custom challenges are available on paid plans. Monthly challenges are created automatically. <a href="/admin/upgrade?from=seasons" style={{ color: 'var(--orange)', fontWeight: 600 }}>Upgrade your plan →</a>
        </div>
      )}

      {/* Active challenge */}
      {activeChallenges.map((s) => (
        <div key={s.id} className="challenge-row active-challenge">
          <div className="challenge-info">
            <h3>{s.name}</h3>
            <div className="challenge-dates">{formatDate(s.start_date)} &mdash; {formatDate(s.end_date)}</div>
          </div>
          <div className="player-actions">
            <span className="challenge-active-badge">Active</span>
            <button className="btn btn-blue btn-sm" onClick={() => viewStandings(s)}>View Standings</button>
            <button className="btn btn-outline btn-sm" onClick={() => openEdit(s)}>Edit</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleArchive(s.id)}>End Challenge</button>
          </div>
        </div>
      ))}

      {activeChallenges.length === 0 && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20, marginBottom: 16 }}>
          No active challenge. Create one and set it active to start tracking.
        </div>
      )}

      {/* Archived challenges */}
      {archivedChallenges.length > 0 && (
        <>
          <h2 style={{ fontSize: '0.95rem', marginTop: 24, marginBottom: 12, color: 'var(--text-muted)' }}>Past Challenges</h2>
          {archivedChallenges.map((s) => {
            const isFuture = new Date((s.start_date || '').split('T')[0] + 'T00:00:00') > new Date();
            return (
              <div key={s.id} className="challenge-row">
                <div className="challenge-info">
                  <h3>{s.name}</h3>
                  <div className="challenge-dates">{formatDate(s.start_date)} &mdash; {formatDate(s.end_date)}</div>
                </div>
                <div className="player-actions">
                  {!isFuture && <button className="btn btn-blue btn-sm" onClick={() => viewStandings(s)}>View Standings</button>}
                  <button className="btn btn-blue btn-sm" onClick={() => handleActivate(s.id)}>Set Active</button>
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(s)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s)}>Delete</button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {challenges.length === 0 && <div className="no-season-msg">No challenges created yet.</div>}

      {/* Add Challenge Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New Challenge</h2>
            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label className="form-label">Challenge Name</label>
                <input className="form-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="e.g. Summer 2026" required />
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={form.start_date} onChange={(e) => handleStartDateChange(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  We recommend challenges of about 11 months. You can run a challenge longer, but a fresh challenge keeps the leaderboard competitive. Leave blank for the default.
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Creating...' : 'Create Challenge'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Challenge Modal */}
      {editingChallenge && (
        <div className="modal-overlay" onClick={() => setEditingChallenge(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Challenge</h2>
            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label className="form-label">Challenge Name</label>
                <input className="form-input" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={editForm.start_date} onChange={(e) => setEditForm({...editForm, start_date: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={editForm.end_date} onChange={(e) => setEditForm({...editForm, end_date: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setEditingChallenge(null)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Past Challenge Standings Modal */}
      {viewingChallenge && (
        <div className="modal-overlay" onClick={() => { setViewingChallenge(null); setPastLeaderboard(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h2>{viewingChallenge.name} &mdash; {viewingChallenge.active ? 'Current' : 'Final'} Standings</h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
              {formatDate(viewingChallenge.start_date)} &mdash; {formatDate(viewingChallenge.end_date)}
            </div>
            {!pastLeaderboard ? (
              <div className="loading"><div className="spinner" /></div>
            ) : pastLeaderboard.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>No player data for this challenge.</p>
            ) : (
              <div>
                {pastLeaderboard.map((p, i) => (
                  <div key={p.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 0', borderBottom: i < pastLeaderboard.length - 1 ? '1px solid var(--card-border)' : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', width: 24 }}>#{i + 1}</span>
                      <div>
                        <div style={{ fontWeight: 600 }}>{p.first_name} {p.last_name}</div>
                        <div style={{ fontSize: '0.75rem', color: p.level?.color || 'var(--text-muted)' }}>{p.level?.name}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: 'var(--orange)' }}>{p.season_points} pts</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Best streak: {p.longest_streak}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setViewingChallenge(null); setPastLeaderboard(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
